import { analysis_reference_area_enum, emission_factor_source_enum } from '@predium/enums';
import isNil from 'lodash/isNil';
import { fromCallback } from 'xstate';
import {
  DEFAULT_DISPLAY_BASE,
  DEFAULT_EMISSION_FACTOR_SOURCE,
  DisplayBase,
} from '@/components/organisms/AnalysisSettings/machines/analysisSettingsMachine';
import {
  LOCALSTORAGE_INCLUDE_NEW_CONSTRUCTION_KEY,
  readIncludeNewConstructionLocalStorageValue,
} from '@/components/organisms/AnalysisSettings/utils/analysisSettingsLocalStorage';

const LOCALSTORAGE_DISPLAY_BASE_KEY = 'analysisSettings.displayBase';
const LOCALSTORAGE_REFERENCE_AREA_DEMAND_KEY = 'analysisSettings.referenceArea.demand';
const LOCALSTORAGE_REFERENCE_AREA_CONSUMPTION_KEY = 'analysisSettings.referenceArea.consumption';
const LOCALSTORAGE_EMISSION_FACTOR_SOURCE_KEY = 'analysisSettings.emissionFactorSource';

type LocalStorageSyncEmittedEvent = {
  type: 'LOCALSTORAGE_INITIALIZED';
  displayBase: DisplayBase;
  referenceArea: analysis_reference_area_enum;
  emissionFactorSource: emission_factor_source_enum;
  includeNewConstruction: boolean;
};
type LocalStorageSyncReceivedEvent = {
  type: 'WRITE_LOCALSTORAGE';
  displayBase: DisplayBase;
  referenceArea: analysis_reference_area_enum;
  emissionFactorSource: emission_factor_source_enum;
  includeNewConstruction: boolean;
};

/**
 * Placeholder registered in `setup({ actors })`. Replaced at runtime via `machine.provide()`.
 */
export const localStorageSyncActorPlaceholder = fromCallback<LocalStorageSyncEmittedEvent, Record<string, never>>(
  () => {},
);

/**
 * Factory that creates the real `fromCallback` localStorage sync actor.
 *
 * Each setting is restored independently: if only one URL param was explicitly set (e.g., a
 * shared link with `?displayBase=absolute`), that setting uses the URL value while the others
 * can still be restored from localStorage.
 *
 * Reference area uses tab-specific localStorage keys so demand and consumption preferences
 * are stored independently. `urlReferenceArea` is null when absent from the URL (no default
 * is registered in nuqs), in which case localStorage preference takes priority.
 *
 * On `WRITE_LOCALSTORAGE`: persists all values from an explicit user Apply action.
 */
export const createLocalStorageSyncActor = (
  urlDisplayBase: DisplayBase,
  urlReferenceArea: analysis_reference_area_enum | null,
  isConsumptionAnalysis: boolean,
  defaultReferenceArea: analysis_reference_area_enum,
  urlEmissionFactorSource: emission_factor_source_enum,
  tracksIncludeNewConstruction: boolean,
  urlIncludeNewConstruction: boolean | null,
  defaultIncludeNewConstruction: boolean,
) =>
  fromCallback<LocalStorageSyncEmittedEvent, Record<string, never>>(({ sendBack, receive }) => {
    const search = new URLSearchParams(window.location.search);
    const isDisplayBaseExplicit = search.has('displayBase');
    const isEmissionFactorSourceExplicit = search.has('emissionFactorSource');
    const isIncludeNewConstructionExplicit = search.has('includeNewConstruction');

    const referenceAreaKey = isConsumptionAnalysis
      ? LOCALSTORAGE_REFERENCE_AREA_CONSUMPTION_KEY
      : LOCALSTORAGE_REFERENCE_AREA_DEMAND_KEY;

    const storedDisplayBaseRaw = localStorage.getItem(LOCALSTORAGE_DISPLAY_BASE_KEY);
    const storedDisplayBase: DisplayBase | null =
      storedDisplayBaseRaw === 'per_square_meter' || storedDisplayBaseRaw === 'absolute' ? storedDisplayBaseRaw : null;

    const storedReferenceAreaRaw = localStorage.getItem(referenceAreaKey);
    const storedReferenceArea: analysis_reference_area_enum | null = Object.values(
      analysis_reference_area_enum,
    ).includes((storedReferenceAreaRaw as analysis_reference_area_enum) ?? '')
      ? (storedReferenceAreaRaw as analysis_reference_area_enum)
      : null;

    const storedEmissionFactorSourceRaw = localStorage.getItem(LOCALSTORAGE_EMISSION_FACTOR_SOURCE_KEY);
    const storedEmissionFactorSource: emission_factor_source_enum | null = Object.values(
      emission_factor_source_enum,
    ).includes((storedEmissionFactorSourceRaw as emission_factor_source_enum) ?? '')
      ? (storedEmissionFactorSourceRaw as emission_factor_source_enum)
      : null;

    const effectiveDisplayBase =
      !isNil(storedDisplayBase) && !isDisplayBaseExplicit
        ? storedDisplayBase
        : (urlDisplayBase ?? DEFAULT_DISPLAY_BASE);

    // If URL has an explicit referenceArea, honour it; otherwise use stored preference or tab default.
    const effectiveReferenceArea = !isNil(urlReferenceArea)
      ? urlReferenceArea
      : (storedReferenceArea ?? defaultReferenceArea);

    const effectiveEmissionFactorSource =
      !isNil(storedEmissionFactorSource) && !isEmissionFactorSourceExplicit
        ? storedEmissionFactorSource
        : (urlEmissionFactorSource ?? DEFAULT_EMISSION_FACTOR_SOURCE);

    const storedIncludeNewConstruction = readIncludeNewConstructionLocalStorageValue();

    const effectiveIncludeNewConstruction = isIncludeNewConstructionExplicit
      ? (urlIncludeNewConstruction as boolean)
      : (storedIncludeNewConstruction ?? defaultIncludeNewConstruction);

    sendBack({
      type: 'LOCALSTORAGE_INITIALIZED',
      displayBase: effectiveDisplayBase,
      referenceArea: effectiveReferenceArea,
      emissionFactorSource: effectiveEmissionFactorSource,
      includeNewConstruction: effectiveIncludeNewConstruction,
    });

    receive((event) => {
      const e = event as unknown as LocalStorageSyncReceivedEvent;
      if (e.type === 'WRITE_LOCALSTORAGE') {
        localStorage.setItem(LOCALSTORAGE_DISPLAY_BASE_KEY, e.displayBase);
        localStorage.setItem(referenceAreaKey, e.referenceArea);
        localStorage.setItem(LOCALSTORAGE_EMISSION_FACTOR_SOURCE_KEY, e.emissionFactorSource);
        if (tracksIncludeNewConstruction) {
          localStorage.setItem(LOCALSTORAGE_INCLUDE_NEW_CONSTRUCTION_KEY, String(e.includeNewConstruction));
        }
      }
    });
  });
